import React, { useState, useReducer, useMemo, ChangeEvent } from 'react';

import { ValidationError } from 'yup';

import moment, { Moment } from 'moment';

import SaveIcon from '@material-ui/icons/Save';
import { List, ListItem, MenuItem, Switch, TextField } from '@material-ui/core';
import { Box } from '@mui/material';
import { Alert } from '@material-ui/lab';
import { TimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import { useStyles } from '../../../../Themable.hooks';

import { useLanguage } from '@/app/state/login/index.hooks';
import {
    DeliveryMethod,
    INewSchedule,
    IReportsDefinition,
    SubjectType,
} from '@/app/state/ui/reportsSlice';
import {
    useProducts,
    useReportsDefinitions,
} from '@/app/state/ui/reportsSlice/index.hooks';

import { restGetTimezoneDictionary } from '@/app/services/registers';

import TranslationHelper from '../../../../../../helpers/TranslationHelper';
import TimeFormatter from '../../../../../../helpers/TimeFormatter';
import { renderMenuItems } from '@/app/helpers/renderMenuItems';

import { reportScheduleSchema } from '@/app/schemas';

import { PERIODICITY_ITEMS } from '@/app/constants/dictionaries/reportPeriodicity';

import IconButtonWithTooltip from '../../../../../../components/IconButtonWithTooltip/IconButtonWithTooltip';
import FieldWithTooltip from '../../../../../../components/FieldWithTooltip';
import PaneHeader from '../../../../../../components/PaneHeader/PaneHeader';
import ObjectsSection from '../../../../../../components/ObjectsSection';
import DatePresets from '../../../../../../components/DatePresets';
import AsyncSelectField from '../../../../../../components/AsyncSelectField';
import EmailsChipsWrapper from '../../../../../../components/formComponents/EmailsChipsWrapper';
import LocationField from '../../../GeneratedReports/components/ReportsForm/LocationField';
import { usePrivileges } from '@/app/state/auth/index.hooks';
import GroupsSection from '@/app/components/GroupsSection';

interface IOwnProps {
    title: string;
    initialState: IInitialState;
    handleClose: () => void;
    handleRequest: (data: INewSchedule, callback: () => void) => void;
    productTags?: string[];
}

export interface IMappedObject {
    id: number;
    key: string;
    name: string;
}
export interface IInitialState {
    reportType: {
        typeId: number | '';
        name: string;
        templateId: number | '';
        isLocationReport: boolean;
        isCustomerServiceReport: boolean;
    };
    periodicity: string;
    triggerTime: Moment | null;
    timeZone: {
        id: number;
        name: string;
    };
    dateTo: Moment;
    dateFrom: Moment;
    objects: IMappedObject[];
    emails?: string[];
    emailsField?: string;
    subjectType: SubjectType;
    deliveryMethod?: DeliveryMethod;
    location: string;
    productIds?: number[] | null;
}

export interface IErrors {
    ['reportType.templateId']?: string;
    periodicity?: string;
    triggerTime?: string;
    timeZone?: string;
    dateTo?: string;
    dateFrom?: string;
    objects?: string;
    deliveryMethod?: string;
}

const KNECT_PRODUCT_TAGS = ['knect'];

const mapPeriodicityItems = (items: string[]) => {
    return items.map((item) => (
        <MenuItem key={item} value={item}>
            {TranslationHelper.translate(item)}
        </MenuItem>
    ));
};
const SchedulesForm = ({
    title,
    handleClose,
    initialState,
    handleRequest,
}: IOwnProps) => {
    const { experimentalFeatures } = usePrivileges();

    const classes = useStyles();
    const [groupsChecked, setGroupsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<IErrors>({});
    const products = useProducts();

    const filteredProducts = useMemo(() => {
        if (!products) {
            return [];
        }
        return products.filter((product) =>
            product.tags?.some((tag) => KNECT_PRODUCT_TAGS.includes(tag))
        );
    }, [products]);

    const language = useLanguage();

    const reportsDefinitions = useReportsDefinitions();

    const [formValues, setFormValues] = useReducer(
        (curVals, newVals) => ({ ...curVals, ...newVals }),
        initialState
    );

    const {
        reportType,
        dateFrom,
        dateTo,
        objects,
        periodicity,
        triggerTime,
        timeZone,
        deliveryMethod,
        location,
        productIds,
    } = formValues;

    const handleFormChange = <T,>(name: string, value: T) => {
        setFormValues({ [name]: value });
    };

    const handleDeliveryMethodChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormValues({ deliveryMethod: e.target.value });
    };

    const handleSubjectTypeChange = (checked: boolean) => {
        setGroupsChecked(checked);
        handleFormChange(
            'subjectType',
            checked ? SubjectType.GROUP : SubjectType.OBJECT
        );
        handleFormChange('objects', []);
    };

    const getRightCustomControls = (submitting: boolean) => (
        <IconButtonWithTooltip
            title={TranslationHelper.translate('Save')}
            onClick={handleValues}
            disabled={submitting}
        >
            <SaveIcon />
        </IconButtonWithTooltip>
    );
    const validateFields = () => {
        reportScheduleSchema
            .validate(formValues, { abortEarly: false })
            .then(() => {
                setErrors({});
            })

            .catch((err: ValidationError) => {
                const newErrors = err.inner.reduce((acc, error) => {
                    acc[error.path || ''] = TranslationHelper.translate(
                        error.errors[0]
                    );
                    return acc;
                }, {});
                setErrors(newErrors);
            });
    };

    const prepareData = (data: IInitialState) => {
        const requestData: INewSchedule = {
            type: Number(data.reportType.typeId),
            repetition: {
                interval: data.periodicity,
                startBoundaryDate: TimeFormatter.dateToShortDateString(
                    data.dateFrom
                ),
                endBoundaryDate: TimeFormatter.dateToShortDateString(
                    data.dateTo
                ),
                triggerTime: data.triggerTime
                    ? data.triggerTime.format('HH:mm')
                    : undefined,
            },
            timeZone: data.timeZone.id,
            subjects: data.objects.map((o) => o.key).join(','),
            subjectType: data.subjectType,
            template: Number(data.reportType.templateId),
            language: language,
            emails: data.emails,
            deliveryMethod: data.deliveryMethod,
            productIds:
                data.productIds && Array.isArray(data.productIds)
                    ? data.productIds.length > 0
                        ? data.productIds.map(Number)
                        : null
                    : data.productIds !== null && data.productIds !== undefined
                    ? [Number(data.productIds)]
                    : null,
        };
        return requestData;
    };

    const handleClearForm = () => {
        setIsSubmitting(false);
        setErrors({});
        handleClose();
    };

    const handleValues = async () => {
        const isDataValid = await reportScheduleSchema.isValid(formValues);

        if (isDataValid) {
            setIsSubmitting(true);

            const data = prepareData(formValues);
            handleRequest(data, handleClearForm);
        } else {
            validateFields();
            setIsSubmitting(false);
        }
    };

    const handleChangePeriodicity = (e: ChangeEvent<HTMLInputElement>) => {
        handleFormChange('periodicity', e.target.value);
        const dateFrom = () => {
            switch (e.target.value) {
                case PERIODICITY_ITEMS.daily:
                    return moment().startOf('day');
                case PERIODICITY_ITEMS.weekly:
                    return moment().day(1 + 7);
                case PERIODICITY_ITEMS.monthly:
                    return moment().startOf('month').add(1, 'month');
                default:
                    return moment().startOf('day');
            }
        };
        handleFormChange('dateFrom', dateFrom());
        handleFormChange('dateTo', dateFrom().add(1, 'day'));
    };

    const handleErrors = (name: string, value: string) => {
        return setErrors({ [name]: value });
    };

    const mapDeliveryMethodsOptions = () => {
        const deliveryMethods = [
            {
                id: DeliveryMethod.NOTIFICATION,
                name: 'Notification',
            },
            { id: DeliveryMethod.ATTACHMENT, name: 'Attachment' },
        ];
        return deliveryMethods.map((dm) => {
            return (
                <MenuItem key={dm.id} value={dm.id}>
                    {TranslationHelper.translate(dm.name, 'wx')}
                </MenuItem>
            );
        });
    };

    return (
        <form onSubmit={handleValues}>
            <PaneHeader
                onCloseClick={handleClose}
                title={TranslationHelper.translate(title)}
                renderRightCustomControls={() =>
                    getRightCustomControls(isSubmitting)
                }
                submitting={isSubmitting}
            />

            <div className={classes.formWrapper}>
                <TextField
                    error={!!errors?.['reportType.templateId']}
                    helperText={errors?.['reportType.templateId']}
                    name="reportType"
                    label={TranslationHelper.translate('Report')}
                    value={reportType.templateId}
                    onChange={(e) => {
                        handleFormChange('objects', initialState.objects);
                        handleFormChange('location', initialState.location);
                        if (reportsDefinitions) {
                            const report = reportsDefinitions.filter(
                                (definition) =>
                                    definition.templateId ===
                                    Number(e.target.value)
                            )[0];
                            handleFormChange('reportType', report);
                        }
                    }}
                    select
                    fullWidth
                    required
                    data-testid={'reportType'}
                    className={classes.field}
                >
                    {renderMenuItems(
                        reportsDefinitions || [],
                        (item: IReportsDefinition) => {
                            return {
                                id: String(item.templateId),
                                name: item.name,
                            };
                        }
                    )}
                </TextField>
                <TextField
                    error={!!errors?.periodicity}
                    helperText={errors?.periodicity}
                    name="periodicity"
                    label={TranslationHelper.translate('Periodicity')}
                    value={periodicity}
                    onChange={handleChangePeriodicity}
                    select
                    fullWidth
                    required
                    data-testid={'periodicity'}
                    className={classes.field}
                >
                    {mapPeriodicityItems(Object.values(PERIODICITY_ITEMS))}
                </TextField>
                <FieldWithTooltip
                    title={TranslationHelper.translate(
                        'To ensure your report includes the latest data, we recommend scheduling it at least one hour after your telemetry units transmit. Please be aware that the trigger time is estimated.'
                    )}
                >
                    <TimePicker
                        value={triggerTime || null}
                        onChange={(val: MaterialUiPickersDate) =>
                            handleFormChange<Moment>(
                                'triggerTime',
                                val as Moment
                            )
                        }
                        ampm={false}
                        label={TranslationHelper.translate('Trigger time')}
                        placeholder="hh:mm"
                        className={classes.field}
                        error={!!errors?.triggerTime}
                        helperText={errors?.triggerTime}
                        fullWidth
                        required
                    />
                </FieldWithTooltip>
                <EmailsChipsWrapper
                    formValues={formValues}
                    handleErrors={handleErrors}
                    handleFormChange={handleFormChange}
                    errors={errors}
                    classes={{ field: classes.fieldMulti }}
                />
                <TextField
                    error={!!errors?.deliveryMethod}
                    helperText={errors?.deliveryMethod}
                    name="deliveryMethod"
                    label={TranslationHelper.translate('Delivery method')}
                    value={deliveryMethod}
                    onChange={handleDeliveryMethodChange}
                    select
                    fullWidth
                    required
                    data-testid={'deliveryMethod'}
                    className={classes.field}
                >
                    {mapDeliveryMethodsOptions()}
                </TextField>
                {deliveryMethod === DeliveryMethod.ATTACHMENT && (
                    <Alert severity="error" className={classes.alert}>
                        <Box className={classes.noticeTitle}>
                            <b>
                                {TranslationHelper.translate(
                                    'Email Recipient and Data Responsibility Notice'
                                )}
                            </b>
                        </Box>
                        <Box>
                            {TranslationHelper.translate(
                                'By proceeding to send this report with an Excel attachment, you acknowledge and accept that:'
                            )}
                        </Box>
                        <List className={classes.list}>
                            <ListItem className={classes.listItem}>
                                {TranslationHelper.translate(
                                    'You are solely responsible for verifying the accuracy of the recipient email address(es).'
                                )}
                            </ListItem>
                            <ListItem className={classes.listItem}>
                                {TranslationHelper.translate(
                                    'You must ensure that the report is transmitted only to individuals authorised to receive such data within your organisation or to other permitted recipients.'
                                )}
                            </ListItem>
                            <ListItem className={classes.listItem}>
                                {TranslationHelper.translate(
                                    'Any disclosure, misdirection, or unauthorised access resulting from incorrect or incomplete email information is your responsibility.'
                                )}
                            </ListItem>
                        </List>
                        <span>
                            {TranslationHelper.translate(
                                'You further confirm that your use and distribution of the data contained in this report comply with all applicable data protection, confidentiality, and internal governance requirements.'
                            )}
                        </span>
                    </Alert>
                )}
                <AsyncSelectField
                    key={'timeZone'}
                    required
                    label={TranslationHelper.translate('Time zone')}
                    getOptions={restGetTimezoneDictionary}
                    error={errors?.['timeZone.name']}
                    onChange={(e, value) => handleFormChange('timeZone', value)}
                    value={timeZone}
                    disableClearable={true}
                />

                <DatePresets
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    handleFormChange={handleFormChange}
                    errors={errors}
                    title="schedule.duration"
                    showButtons={false}
                    dateOnly
                    disablePast
                />
                {experimentalFeatures && (
                    <TextField
                        name="productIds"
                        label={TranslationHelper.translate('Product')}
                        value={
                            Array.isArray(productIds)
                                ? productIds.length > 0
                                    ? productIds[0]
                                    : ''
                                : productIds ?? ''
                        }
                        onChange={(e) => {
                            const val = e.target.value;
                            handleFormChange(
                                'productIds',
                                val === '' ? null : [Number(val)]
                            );
                        }}
                        select
                        fullWidth
                        data-testid={'productIds'}
                        className={classes.field}
                    >
                        <MenuItem value="">{''}</MenuItem>
                        {filteredProducts.map((product) => (
                            <MenuItem key={product.id} value={product.id}>
                                {product.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {experimentalFeatures && (
                    <Box className={classes.switch}>
                        {TranslationHelper.translate('Objects')}
                        <Switch
                            onChange={(e) =>
                                handleSubjectTypeChange(e.target.checked)
                            }
                            checked={groupsChecked}
                        />
                        {TranslationHelper.translate('Groups')}
                    </Box>
                )}
                {reportType.isLocationReport ? (
                    <LocationField
                        errors={errors.objects}
                        location={location}
                        onChange={handleFormChange}
                        subjectsKey="id"
                        extended={reportType.isCustomerServiceReport}
                    />
                ) : (
                    <>
                        {groupsChecked ? (
                            <GroupsSection
                                selectedGroups={objects}
                                handleFormChange={handleFormChange}
                                errors={errors?.objects}
                            />
                        ) : (
                            <ObjectsSection
                                objects={objects}
                                handleFormChange={handleFormChange}
                                errors={errors?.objects}
                            />
                        )}
                    </>
                )}
            </div>
        </form>
    );
};

export default SchedulesForm;
